import { DateRangePickerField, DecimalField } from '@ff-it/form';
import { Box, Button, Stack } from '@ff-it/ui';
import { memo, ReactElement, useEffect, useRef } from 'react';
import { required } from 'utilities';
import { quantityField, periodField, amountField } from './style.css';
import { PositionBreakdown } from './types';
import equal from 'fast-deep-equal';
import { Breakdown } from './Breakdown';
import { SeekButton } from './SeekButton';

type PositionRowProps = {
  name: string;
  index: number;
  remove: (index: number) => void;
  minDate: Date;
  maxDate: Date;
  active?: boolean;
  breakdown?: PositionBreakdown;
  seekBudgetQuantity: (index: number, value: { budget: string }) => void;
};

export const PositionRow = memo(function PositionRow({
  name,
  index,
  remove,
  minDate,
  maxDate,
  active,
  breakdown,
  seekBudgetQuantity,
}: PositionRowProps): ReactElement {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (active && ref.current) {
      setTimeout(() => {
        ref.current?.focus();
      }, 0);
    }
  }, [active]);
  return (
    <Stack
      direction="horizontal"
      p="2"
      testId="position-row"
      aria-selected={active}
      border={active ? 'primary' : 'subtle'}
      borderRadius="md"
    >
      <Box className={periodField}>
        <DateRangePickerField
          fromName={`${name}.date_from`}
          toName={`${name}.date_to`}
          required
          validate={required}
          className="mb-0"
          size="sm"
          minDate={minDate}
          maxDate={maxDate}
        />
      </Box>
      <Box className={quantityField}>
        <Stack direction="horizontal" space="0.5">
          <DecimalField
            name={`${name}.quantity`}
            required
            validate={required}
            precision={4}
            className="mb-0 text-right"
            size="sm"
            ref={ref}
          />

          <SeekButton seekBudgetQuantity={(amount) => seekBudgetQuantity(index, amount)} />
        </Stack>
      </Box>
      <Box className={amountField}>{breakdown && <Breakdown {...breakdown} />}</Box>
      <Box display="flex" alignItems="center">
        <Button size="xs" variant="ghost" icon="trash" onClick={() => remove(index)} />
      </Box>
    </Stack>
  );
}, equal);
